import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Button from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateVariantSelection from "@NA/Handlers/ADIUpdateVariantSelection";
import filterUnusedTraits from "@NA/Hooks/useFilterUnusedVariantTraits";
import filterSelectedTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { EddContext } from "@NA/Utils/EddUtility";

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId & 
    HasHistory;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const currentSplitPath = getLocation(state).pathname.split("/");
    const { search } = getLocation(state);
    const productUrl = currentSplitPath[currentSplitPath.length - 1];
    currentSplitPath.pop();
    return {
        search,
        productUrl,
        currentPath: currentSplitPath.join("/"),
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        variantSelection: state.pages.productDetails.variantSelection,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
        selectedProductId: state.pages.productDetails.selectedProductId,
    };
};

const mapDispatchToProps = {
    updateVariantSelection,
};

export interface ProductDetailsVariantOptionsStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    button?: InjectableCss;
    selected?: InjectableCss;
}

export const variantOptionsStyles: ProductDetailsVariantOptionsStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 0px;
            margin-bottom: 12px;
        `,
    },
    label: {
        css: css`
            display: block;
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 4px;
        `,
    },
    button: {
        css: css`
            min-width: 28px;
            min-height: 32px;
            margin: 0px 10px 10px 0px;
            line-height: normal;
            padding: 6.5px 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            color: #666;
            font-size: 14px;
            font-weight: bold;
            span {
                font-size: inherit;
                font-weight: unset;
            }
            &:focus {
                outline-offset: 0px;
                outline-color: initial;
                outline-style: solid;
                outline-width: 0px;
            }
            &:hover {
                background-color: #c9ddf5;
                color: #004da6;
                border: solid 1px #004da6;
            }
        `,
    },
};

const styles = variantOptionsStyles;

const selectedStyle = {
    border: "2px solid #006ae3",
};
const selectedHoverStyle = {
    backgroundColor: "initial",
    color: "#666",
};

const filterStyle = {
    border: "solid 1px #ddd",
    backgroundColor: "#f7f7f7",
    color: "#bbb",
};
const filterHoverStyle = {
    backgroundColor: "#c9ddf5 !important",
    color: "#004da6 !important",
    border: "solid 1px #004da6 !important",
};

const ProductDetailsVariantOptions: React.FC<Props> = ({
    productUrl,
    currentPath,
    variantChildren,
    variantSelection,
    updateVariantSelection,
    variantTraits,
    parentProductId,
    variantProductState,
    selectedProductId,
    search,
    history
}) => {
    const [utmVisited, setUtmVisited] = useState<boolean>(false);
    const eddContext = React.useContext(EddContext);

    useEffect(() => {
        if (
            variantProductState.value &&
            (variantProductState.value.isVariantParent ||
                variantProductState.value.childTraitValues ||
                variantProductState.value.variantTypeId) && 
                variantProductState.value.canonicalUrl.includes(productUrl)
        ) {
            const parsedQuery =
                parseQueryString<{
                    option?: string;
                    utm_term?: string;
                    utm_content?: string;
                    utm_campaign?: string;
                    utm_medium?: string;
                    utm_source?: string;
                }>(search);
            // routed from master product url
            if (
                variantProductState.value.isVariantParent &&
                !variantProductState.value.childTraitValues &&
                variantChildren
            ) {
                let productPath = variantChildren[0].urlSegment;
                if (!productPath.includes("?option=") && variantChildren[0].canonicalUrl.includes("?option=")) {
                    productPath = variantChildren[0].canonicalUrl.replace("/product/", "").replace("/Product/", "");
                }
                history.replace(`${currentPath}/${productPath}`);
            }
            // routed from child product url
            else if (
                variantProductState.value.variantTypeId &&
                !variantProductState.value.isVariantParent &&
                !variantProductState.value.childTraitValues
            ) {
                history.replace(`${currentPath}/${variantProductState.value.urlSegment}`);
            }
            // routed from selection or plp
            else {
                let productPath = variantProductState.value.urlSegment;
                if (!productPath.includes("?option=") && variantProductState.value.canonicalUrl.includes("?option=")) {
                    productPath = variantProductState.value.canonicalUrl
                        .replace("/product/", "")
                        .replace("/Product/", "");
                }
                if (
                    (!parsedQuery.utm_campaign &&
                        !parsedQuery.utm_content &&
                        !parsedQuery.utm_medium &&
                        !parsedQuery.utm_source &&
                        !parsedQuery.utm_term) ||
                    utmVisited
                ) {
                    window.history.replaceState(null, "", `${currentPath}/${productPath}`);
                } else {
                    const utmQuery = Object.entries(parsedQuery)
                        .filter(key => key[0].toLowerCase() !== "option")
                        .map(([key, value]) => `${key}=${value}`)
                        .join("&");
                    setUtmVisited(true);
                    window.history.replaceState(null, "", `${currentPath}/${productPath}&${utmQuery}`);
                }
            }
        }
    }, [selectedProductId]);

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        eddContext.value = true;
        eddContext.id = traitValueId;
        updateVariantSelection({ traitValueId, variantTraitId, productId: parentProductId });
    };

    if (!variantTraits || variantTraits.length === 0 || !variantChildren) {
        return null;
    }

    const traits = filterUnusedTraits(variantTraits, variantChildren);
    const filteredTraits = filterSelectedTraits(variantTraits, variantChildren, variantSelection);

    return (
        <StyledWrapper {...styles.wrapper}>
            {traits.map(
                variantTrait =>
                    !!variantTrait.traitValues?.length && (
                        <div key={variantTrait.id}>
                            <Typography {...styles.label}>
                                {translate(variantTrait.nameDisplay)}:{" "}
                                {translate(
                                    variantTrait.traitValues.find(v => v.id === variantSelection[variantTrait.id])
                                        ?.valueDisplay || "",
                                )}
                            </Typography>
                            {variantTrait.traitValues
                                ?.sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(traitValue => {
                                    const selectedProduct = variantSelection[variantTrait.id] === traitValue.id;
                                    const filteredProduct =
                                        filteredTraits
                                            .find(t => t.id === variantTrait.id)
                                            ?.traitValues?.findIndex(v => v.id === traitValue.id) === -1;

                                    return (
                                        <Button
                                            sizeVariant="small"
                                            buttonType="outline"
                                            variant="secondary"
                                            style={selectedProduct ? selectedStyle : filteredProduct ? filterStyle : {}}
                                            hoverStyle={
                                                selectedProduct
                                                    ? selectedHoverStyle
                                                    : filteredProduct
                                                    ? filterHoverStyle
                                                    : {}
                                            }
                                            {...styles.button}
                                            onClick={() => variantChangeHandler(traitValue.id, variantTrait.id)}
                                        >
                                            {translate(traitValue.valueDisplay)}
                                        </Button>
                                    );
                                })}
                        </div>
                    ),
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductDetailsVariantOptions))),
    definition: {
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
